import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, mergeProps as _mergeProps, withCtx as _withCtx, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, isRef as _isRef } from "vue"

const _hoisted_1 = { class: "map-container" }
const _hoisted_2 = {
  key: 0,
  class: "map-legend"
}
const _hoisted_3 = { class: "location-and-sharing" }
const _hoisted_4 = { class: "slider-row mx-16 mt-12" }
const _hoisted_5 = { class: "thumb-label" }
const _hoisted_6 = { class: "d-flex flex-row" }

import { computed, ref, toRaw, useTemplateRef, watch, type Ref, type WritableComputedRef } from "vue";
import { useDisplay } from 'vuetify';
import { storeToRefs } from "pinia";
import { MapBoxFeature, MapBoxFeatureCollection, MapBoxFeatureType, MapBoxForwardGeocodingOptions, geocodingInfoForSearch } from "@cosmicds/vue-toolkit";
import { Map, GeoJSONSource, type StyleLayer } from "maplibre-gl";
import { getTimezoneOffset } from "date-fns-tz";
import { v4 } from "uuid";

import type { LatLngPair, PointSelectionInfo, RectangleSelectionInfo, SelectionType } from "@/types";
import { type MoleculeType, MOLECULE_OPTIONS } from "@/esri/utils";
import { colorbarOptions } from "@/esri/ImageLayerConfig";
import { colormap } from "@/colormaps/utils";
import { useTempoStore } from "@/stores/app";
import { useLocationMarker } from "@/composables/maplibre/useMarker";
import { useRectangleSelection } from "@/composables/maplibre/useRectangleSelection";
import { addRectangleLayer, addPointLayer, regionBounds, fitBounds, removeRectangleLayer, removePointLayer } from "@/composables/maplibre/utils";
import { usePointSelection } from "@/composables/maplibre/usePointSelection";
import { COLORS } from "@/utils/color";
import { EsriSampler } from "@/esri/services/sampling";
import { useMultiMarker } from '@/composables/maplibre/useMultiMarker';

import { setLayerOpacity, setLayerVisibility } from "@/maplibre_controls";

import EsriMap from "@/components/EsriMap.vue";
import MapColorbarWrap from "@/components/MapColorbarWrap.vue";

import { addPowerPlants } from "@/composables/addPowerPlants";
import { addHMSFire } from "@/composables/addHMSFire";

import { addQUI } from '@/composables/addAQI';

// base it of singleDateSelected
import { addPopulationDensityLayer } from '@/composables/addPopulationDensity';
import { addLandUseLayer } from "@/composables/addLandUse";
import { type UseEsriLayer, useEsriLayer } from "@/esri/maplibre/useEsriImageLayer";
// just use the hcho layer for now
import { stretches, colorramps, type ColorRamps } from "@/esri/ImageLayerConfig";
type MapType = Map | null;
type MapTypeRef = Ref<MapType>;
type Timeout = ReturnType<typeof setTimeout>;

type UnifiedRegionType = typeof regions.value[number];

type ColorbarOptionsKey = keyof typeof colorbarOptions;

export default /*@__PURE__*/_defineComponent({
  __name: 'MapWithControls',
  setup(__props) {

/* eslint-disable @typescript-eslint/no-unused-vars */
const maplibreMap = useTemplateRef<InstanceType<typeof EsriMap>>("maplibreMap");
const map = ref<MapType>(null);

const mapID = `map-${v4().replace("-", "")}`;

const store = useTempoStore();
const {
  regions,
  regionOpacity,
  regionVisibility,
  timestamp,
  timeIndex,
  minIndex,
  maxIndex,
  date,
  selectedTimezone,
  timeSliderUsedCount,
  playButtonClickedCount,
  timestamps,
  timestampsLoaded,
  selectionActive,
  regionsCreatedCount,
  maxSampleCount,
  focusRegion,
  initState,
  homeState,
  showFieldOfRegard,
  showRoads,
  showSamplingPreviewMarkers,
  singleDateSelected,
  showAdvancedLayers,
  showRGBMode,
} = storeToRefs(store);

const molecule = ref<MoleculeType>("no2");
const currentTempoDataService = computed(() => store.getTempoDataService(molecule.value));

function createSelectionComputed(selection: SelectionType): WritableComputedRef<boolean> {
  return computed({
    get() {
      return selectionActive.value === selection;
    },
    set(value: boolean) {
      if (value === (selectionActive.value !== selection)) {
        selectionActive.value = value ? selection : null;
      }
    },
  });
}

const pointSelectionActive = createSelectionComputed("point");
const rectangleSelectionActive = createSelectionComputed("rectangle");

const { selectionInfo: rectangleInfo } = useRectangleSelection(map as MapTypeRef, "red", rectangleSelectionActive);
const { selectionInfo: pointInfo } = usePointSelection(map as MapTypeRef, pointSelectionActive);

const display = useDisplay();

const pp = addPowerPlants(map as Ref<Map | null> | null, false);
const airQualityUrl = computed(() => {
  const date = store.singleDateSelected;
  if (!date) {
    return 'https://s3-us-west-1.amazonaws.com/files.airnowtech.org/airnow/2025/20250914/KMLPointMaps_PM2.5-24hr.kml';
  }

  const year = date.getUTCFullYear();
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = date.getUTCDate().toString().padStart(2, '0');
  return `https://s3-us-west-1.amazonaws.com/files.airnowtech.org/airnow/${year}/${year}${month}${day}/KMLPointMaps_PM2.5-24hr.kml`;
});
const aqiLayer = addQUI(airQualityUrl.value, { 
  propertyToShow: 'aqi', 
  labelMinZoom: 5, 
  layerName: 'aqi', 
  visible: false,
  showLabel: true, 
  showPopup: true
});

// Ensure date/url changes trigger a reload, even if initial load failed
watch(airQualityUrl, (newUrl) => {
  aqiLayer.setUrl(newUrl).catch(() => {/* ignore */});
});


const popLayer = addPopulationDensityLayer();

const sentinalLandUseLayer = addLandUseLayer();

const hmsFire = addHMSFire(singleDateSelected, {
  layerName: 'hms-fire',
  visible: false,
  showPopup: true,
  showLabel: false,
});

const hchoLayer = useEsriLayer('hcho', timestamp, 1, true, 'tempo-hcho', false);
const ozoneLayer = useEsriLayer('o3', timestamp, 1, true, 'tempo-o3', false);
const no2Layer = ref<UseEsriLayer | null>(null);

function addAdvancedLayers(m: Map | null) {
  if (m === null) return;
  // pp.addheatmapLayer();
  // pp.togglePowerPlants(false);
  aqiLayer.addToMap(m);
  popLayer.addEsriSource(m);
  sentinalLandUseLayer.addEsriSource(m);
  hmsFire.addToMap(m);
  hchoLayer.addEsriSource(m);
  ozoneLayer.addEsriSource(m);
  // Only move if target layer exists (avoid errors if initial KML load failed)
  try {
    if (m.getLayer('kml-layer-aqi')) {
      m.moveLayer('states-custom','kml-layer-aqi');
    }
  } catch {
    // ignore
  }
  
  pp.addLayer();
  // pp.togglePowerPlants(false);
}

function removeAdvancedLayers(m: Map | null) {
  if (m === null) return;
  aqiLayer.removeFromMap(m);
  popLayer.removeEsriSource();
  sentinalLandUseLayer.removeEsriSource();
  hmsFire.removeFromMap(m);
  hchoLayer.removeEsriSource();
  ozoneLayer.removeEsriSource();
  pp.removeLayer();
}

const onMapReady = (m: Map) => {
  console.log('Map ready event received');
  map.value = m; // ESRI source already added by EsriMap
  if (showAdvancedLayers.value) addAdvancedLayers(m);
  updateRegionLayers(regions.value);
};

watch(showAdvancedLayers, (value) => {
  if (value) {
    addAdvancedLayers(map.value as Map | null);
    return;
  }
  removeAdvancedLayers(map.value as Map | null);
  
  
});

watch(molecule, (newMolecule) => {
  if (map.value) {
    hchoLayer.setVisibility(newMolecule === 'hcho');
    ozoneLayer.setVisibility(newMolecule === 'o3');
    no2Layer.value?.setVisibility(newMolecule === 'no2');
    // map.value.moveLayer(`tempo-${newMolecule}`, 'tempo-no2');
  }
});

const activeLayer = computed(() => `tempo-${molecule.value}`);

const rgbstretches = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'NO2_Troposphere': [0, 7_500_000_000_000_000],
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'Ozone_Column_Amount': [250, 430], // +- 2 sigma
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'HCHO': [1_000_000_000_000_000, 15_000_000_000_000_000],
} as Record<string, [number, number]>;
const rgbcolorramps = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'NO2_Troposphere': 'magentafromwhite',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'Ozone_Column_Amount': 'cyanfromwhite', 
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'HCHO': 'yellowfromwhite',
} as Record<string, ColorRamps>;
  
watch(showRGBMode, (cMode) => {
  
  hchoLayer.renderOptions.value.colormap = (cMode ? rgbcolorramps : colorramps)['HCHO'];
  ozoneLayer.renderOptions.value.colormap = (cMode ? rgbcolorramps : colorramps)['Ozone_Column_Amount'];
  if (no2Layer.value) {
    no2Layer.value.renderOptions.colormap = (cMode ? rgbcolorramps : colorramps)['NO2_Troposphere'];
  }
  hchoLayer.renderOptions.value.range = (cMode ? rgbstretches : stretches)['HCHO'];
  ozoneLayer.renderOptions.value.range = (cMode ? rgbstretches : stretches)['Ozone_Column_Amount'];
  if (no2Layer.value) {
    no2Layer.value.renderOptions.range = (cMode ? rgbstretches : stretches)['NO2_Troposphere'];
  }

});

const showLocationMarker = ref(true);
const {
  setMarker,
  removeMarker,
  locationMarker,
} = useLocationMarker(map as Ref<Map | null>, showLocationMarker.value);

const opacity = ref(0.9);
const playing = ref(false);
const playInterval = ref<Timeout | null>(null);


const searchOpen = ref(true);
const searchErrorMessage = ref<string | null>(null);
function activateRectangleSelectionMode() {
  rectangleSelectionActive.value = !rectangleSelectionActive.value;
}

function activatePointSelectionMode() {
  pointSelectionActive.value = !pointSelectionActive.value;
}

const regionLayers: Record<string, GeoJSONSource> = {};

// const colorMap = computed(() => colorbarOptions[molecule.value].colormap.toLowerCase());
const colorMap = computed(() => {
  const mol = molecule.value == 'no2' 
    ? 'NO2_Troposphere' : molecule.value == 'hcho' 
      ? 'HCHO' : 'Ozone_Column_Amount';
  return showRGBMode.value ? rgbcolorramps[mol].toLowerCase() : colorramps[mol].toLowerCase();
});

const currentColorbarOptions = computed<typeof colorbarOptions[ColorbarOptionsKey]>(() => {
  const mol = molecule.value == 'no2' 
    ? 'NO2_Troposphere' : molecule.value == 'hcho' 
      ? 'HCHO' : 'Ozone_Column_Amount';
  return {
    ...colorbarOptions[molecule.value],
    colormap: showRGBMode.value ? rgbcolorramps[mol] : colorramps[mol],
    stretch: showRGBMode.value ? rgbstretches[mol] : stretches[mol],
  };
});

watch(currentColorbarOptions, (cc) => {
  console.log('current colorbar options changed to', cc);
});

const currentColormap = computed(() => {
  return (x: number): string => {
    let rgb: number[] = [128, 128, 128];
    try {
      rgb = colormap(colorMap.value as AllAvailableColorMaps, 0, 1, x);
    }
    catch {
      console.log("no valid colormap. returning gray");
    }
    return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]},1)`;
  };
});

watch(colorMap, (value) => {
  console.log('color map changed to', value);
});

const mapTitle = computed(() => {
  const currentMolecule = MOLECULE_OPTIONS.find(m => m.value === molecule.value);
  if (!currentMolecule) {
    return '';
  }
  return currentMolecule.title;
});

// TODO: Maybe there's a built-in Date function to get this formatting?
const thumbLabel = computed(() => {
  if (date.value === null || timestamp.value === null) {
    return '';
  }
  const offset = getTimezoneOffset(selectedTimezone.value, date.value);
  const dateObj = new Date(timestamp.value + offset);
  const hours = dateObj.getUTCHours();
  const amPm = hours >= 12 ? "PM" : "AM";
  let hourValue = hours % 12;
  if (hourValue === 0) {
    hourValue = 12;
  }
  return `${date.value.getUTCMonth() + 1}/${dateObj.getUTCDate()}/${dateObj.getUTCFullYear()} ${hourValue}:${dateObj.getUTCMinutes().toString().padStart(2, '0')} ${amPm}`;
});

const defaultMapboxOptions = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  types: ["place", "postcode"] as MapBoxFeatureType[],
  countries: ["US", "CA", "MX", "CU", "BM", "HT", "DO"],
  limit: 5,
};
async function geocodingInfoForSearchLimited(searchText: string, options?: MapBoxForwardGeocodingOptions): Promise<MapBoxFeatureCollection | null> {
  const token = process.env.VUE_APP_MAPBOX_ACCESS_TOKEN;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const opts = options ?? { ...defaultMapboxOptions, access_token: token ?? "" };
  return geocodingInfoForSearch(searchText, opts).catch(_err => null);
}

function setLocationFromSearch(items: [MapBoxFeature | null, string]) {
  const [feature, text] = items;
  if (feature !== null) {
    // Latitude, Longitude order
    const coordinates: LatLngPair = [feature.center[1], feature.center[0]] as LatLngPair;
    maplibreMap.value?.setView(coordinates, 12);
    setMarker(coordinates);
    store.userSelectedLocations.push(text);
  }
}

function onZoomhome() {
  if (locationMarker.value !== null) {
    removeMarker();
  }
}

const currentUrl = ref(window.location.href);
function updateURL() {
  if (map.value) {
    const center = map.value.getCenter();
    let stateObj = null as Record<string, string> | null;
    stateObj = {
      lat: `${center.lat.toFixed(4)}`,
      lon: `${center.lng.toFixed(4)}`,
      zoom: `${map.value.getZoom()}`,
      t: `${timestamp.value}`
    };
    const url = new URL(location.origin);
    const searchParams = new URLSearchParams(stateObj ?? {});
    // const hash = window.location.hash;
    // url.hash = hash;
    url.pathname = location.pathname;
    window.history.replaceState(null, '', url.toString());
    url.search = searchParams.toString();
    currentUrl.value = url.toString();
    // window.history.replaceState(stateObj, '', url);
  }
}

// ESRI timesteps arrive from EsriMap component; store directly in timestamps
function onEsriTimestepsLoaded(steps: number[]) {
  if (!Array.isArray(steps) || steps.length === 0) return;
  const sorted = steps.slice().sort();
  timestamps.value = sorted;
  if (timeIndex.value >= sorted.length) {
    timeIndex.value = 0;
  }
  timestampsLoaded.value = true;
}

watch(playing, (val: boolean) => {
  if (val) {
    play();
    playButtonClickedCount.value += 1;
  } else {
    pause();
  }
});

function play() {
  playInterval.value = setInterval(() => {
    if (timeIndex.value >= maxIndex.value) {
      if (playInterval.value) {
        // clearInterval(this.playInterval);
        // this.playInterval = null;
        // let it loop
        timeIndex.value = minIndex.value;
      }
    } else {
      timeIndex.value += 1;
    }
  }, 1000);
}


function pause() {
  if (playInterval.value) {
    clearInterval(playInterval.value);
  }
}

function rectangleIsDegenerate(info: RectangleSelectionInfo): boolean {
  return info.xmax === info.xmin || info.ymax === info.ymin;
}

function addLayer(
  info: RectangleSelectionInfo | PointSelectionInfo,
  geometryType: "rectangle" | "point",
  color: string,
): { layer: GeoJSONSource } {
  const isRect = geometryType === 'rectangle';
  const layerInfo = isRect ?
    addRectangleLayer((map.value as MapType)!, info as RectangleSelectionInfo, color, regionOpacity.value, regionVisibility.value) :
    addPointLayer((map.value as MapType)!, info as PointSelectionInfo, color, regionVisibility.value);
  map.value?.moveLayer(layerInfo.layer.id);
  return layerInfo;
}

function removeLayer(
  layer: StyleLayer,
  geometryType: "rectangle" | "point",
) {
  const isRect = geometryType === 'rectangle';
  if (isRect) {
    removeRectangleLayer((map.value as MapType)!, layer);
  } else {
    removePointLayer((map.value as MapType)!, layer);
  }
}

function createRegion(info: RectangleSelectionInfo | PointSelectionInfo, geometryType: "rectangle" | "point"): UnifiedRegionType {
  const color = COLORS[regionsCreatedCount.value % COLORS.length];
  regionsCreatedCount.value += 1;

  const id = v4();
  const { layer } = addLayer(info, geometryType, color);
  regionLayers[id] = layer;
  return {
    id,
    name: `${geometryType === "rectangle" ? 'Region' : 'Point'} ${regionsCreatedCount.value}`,
    geometryInfo: toRaw(info),
    geometryType: geometryType,
    color,
  };
}

function getRegionsDifference(arr1: UnifiedRegionType[], arr2: UnifiedRegionType[]): UnifiedRegionType[] {
  const ids2 = arr2.map(r => r.id);
  return arr1.filter(element => !ids2.includes(element.id));
}

let existingRegions: UnifiedRegionType[] = [];
function updateRegionLayers(newRegions: UnifiedRegionType[]) {
  const added = getRegionsDifference(newRegions, existingRegions);
  const removed = getRegionsDifference(existingRegions, newRegions);
  added.forEach(region => {
    if (map.value && !regionLayers[region.id]) {
      const { layer } = addLayer(region.geometryInfo, region.geometryType, region.color);
      regionLayers[region.id] = layer;
    }
  });

  removed.forEach(region => {
    if (map.value && regionLayers[region.id]) {
      removeLayer(regionLayers[region.id] as unknown as StyleLayer, region.geometryType);
      delete regionLayers[region.id];
    }
  });
  existingRegions = [...newRegions];
}

watch(regions, updateRegionLayers, { deep: true });

watch(regionOpacity, (opacity: number) => {
  if (map.value !== null) {
    Object.values(regionLayers).forEach(layer => {
      setLayerOpacity(map.value as Map, layer.id, opacity);
    });
    setLayerOpacity(map.value as Map, "predicted-samples-locations-layer", opacity);
  }
});

watch(regionVisibility, (visible: boolean) => {
  if (map.value !== null) {
    Object.values(regionLayers).forEach(layer => {
      setLayerVisibility(map.value as Map, layer.id, visible);
    });
    setLayerVisibility(map.value as Map, "predicted-samples-locations-layer", visible);
  }
});

watch(rectangleInfo, (info: RectangleSelectionInfo | null) => {
  if (info === null || map.value === null) {
    rectangleSelectionActive.value = false;
    return;
  }
  if (rectangleIsDegenerate(info)) {
    // make it a point selection instead
    // TODO: only implement when we have a solution to only do this on a double-click
    // pointInfo.value = {
    //   x: info.xmin,
    //   y: info.ymin
    // };
    rectangleSelectionActive.value = false;
    return;
  }

  const newRegion = createRegion(info, "rectangle");
  store.addRegion(newRegion);
  rectangleSelectionActive.value = false;
  
  // do not permit editing a region on a selection
  // handleSelectionRegionEdit(info);
  

});

// Add watcher for point selection
watch(pointInfo, (info: PointSelectionInfo | null) => {
  if (info === null || map.value === null) {
    pointSelectionActive.value = false;
    return;
  }
  const newRegion = createRegion(info, "point"); 
  store.addRegion(newRegion);
  pointSelectionActive.value = false;
});

const samplingPreviewMarkers = useMultiMarker(map as MapTypeRef , {
  shape: 'circle',
  color: '#0000ff',
  fillColor: '#0000ff',
  fillOpacity: 0.5,
  opacity: regionOpacity.value,
  radius: 0.02 / 2, // degrees
  scale: 'world',
  outlineColor: '#0000ff',
  label: 'predicted-samples-locations'
});

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const sampler = ref<EsriSampler>(null);
currentTempoDataService.value.withMetadataCache().then(meta => {
  sampler.value = new EsriSampler(meta);
});

watch([showSamplingPreviewMarkers, regions, ()=> regions.value.length], (newVal) => {
  const tempoDataService = currentTempoDataService.value;
  const show = newVal[0];
  const regs = newVal[1];
  samplingPreviewMarkers.clearMarkers();
  let locations: {x: number, y:number}[] = [];
  if (sampler.value && show && regs.length > 0) {
    regs.forEach(r => {
      if (r.geometryType === 'rectangle') {
        sampler.value.setGeometry(r.geometryInfo);
        if (tempoDataService.meta) {
          sampler.value.setMetadata(tempoDataService.meta);
        }
        locations = [...locations, ...sampler.value.getSampleLocationsGrid(maxSampleCount.value)];
        // samplingPreviewMarkers.addMarkers(locations);
      }
    });
    samplingPreviewMarkers.addMarkers(locations);
  }
});

// TODO: This may need to be revisited when there are two maps
watch(focusRegion, region => {
  if (region !== null) {
    const bounds = regionBounds(region);
    fitBounds(map.value, bounds, true);
    focusRegion.value = null;
  }
});


return (_ctx: any,_cache: any) => {
  const _component_v_toolbar_title = _resolveComponent("v-toolbar-title")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!
  const _component_v_toolbar = _resolveComponent("v-toolbar")!
  const _component_location_search = _resolveComponent("location-search")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_slider = _resolveComponent("v-slider")!
  const _component_icon_button = _resolveComponent("icon-button")!
  const _component_map_controls = _resolveComponent("map-controls")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(MapColorbarWrap, {
      horizontal: _unref(display).width.value <= 750,
      "current-colormap": currentColormap.value,
      "color-map": colorMap.value,
      "start-value": currentColorbarOptions.value.stretch[0] / currentColorbarOptions.value.cbarScale,
      "end-value": currentColorbarOptions.value.stretch[1] / currentColorbarOptions.value.cbarScale,
      "molecule-label": currentColorbarOptions.value.label,
      "cbar-scale": currentColorbarOptions.value.cbarScale
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, {
          class: "map-contents",
          style: {"width":"100%","height":"100%"}
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_toolbar, {
              density: "compact",
              color: "var(--info-background)"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_toolbar_title, {
                  text: `TEMPO Data Viewer: ${mapTitle.value}`
                }, null, 8, ["text"]),
                _createVNode(_component_v_tooltip, {
                  text: _unref(selectionActive) === 'rectangle' ? 'Cancel selection' : 'Select a region'
                }, {
                  activator: _withCtx(({ props }) => [
                    _createVNode(_component_v_btn, _mergeProps(props, {
                      icon: "mdi-select",
                      color: _unref(selectionActive) === 'rectangle' ? 'info' : 'default',
                      variant: _unref(selectionActive) === 'rectangle' ? 'tonal' : 'text',
                      disabled: _unref(selectionActive) === 'point',
                      onClick: activateRectangleSelectionMode
                    }), null, 16, ["color", "variant", "disabled"])
                  ]),
                  _: 1
                }, 8, ["text"]),
                _createVNode(_component_v_tooltip, {
                  text: _unref(selectionActive) === 'point' ? 'Cancel selection' : 'Select a point'
                }, {
                  activator: _withCtx(({ props }) => [
                    _createVNode(_component_v_btn, _mergeProps(props, {
                      icon: "mdi-plus",
                      color: _unref(selectionActive) === 'point' ? 'info' : 'default',
                      variant: _unref(selectionActive) === 'point' ? 'tonal' : 'text',
                      disabled: _unref(selectionActive) === 'rectangle',
                      onClick: activatePointSelectionMode
                    }), null, 16, ["color", "variant", "disabled"])
                  ]),
                  _: 1
                }, 8, ["text"])
              ]),
              _: 1
            }),
            _createVNode(EsriMap, {
              mapID: mapID,
              initial: _unref(initState),
              home: _unref(homeState),
              "show-roads": _unref(showRoads),
              events: {
            'moveend': updateURL,
            'zoomend': updateURL,
          },
              timestamp: _unref(timestamp),
              molecule: "no2",
              opacity: opacity.value,
              "show-field-of-regard": _unref(showFieldOfRegard),
              onZoomhome: onZoomhome,
              onReady: onMapReady,
              onEsriLayer: _cache[0] || (_cache[0] = ($event: any) => (no2Layer.value = $event)),
              onEsriTimestepsLoaded: onEsriTimestepsLoaded,
              ref_key: "maplibreMap",
              ref: maplibreMap,
              width: "100%",
              height: "450px",
              "maplibre-layer-name": "tempo-no2"
            }, null, 8, ["initial", "home", "show-roads", "events", "timestamp", "opacity", "show-field-of-regard"]),
            (_unref(showFieldOfRegard))
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, [...(_cache[7] || (_cache[7] = [
                  _createElementVNode("hr", { class: "line-legend" }, null, -1),
                  _createTextVNode("TEMPO Field of Regard", -1)
                ]))]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_3, [
              _createVNode(_component_location_search, {
                modelValue: searchOpen.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((searchOpen).value = $event)),
                small: "",
                "stay-open": "",
                buttonSize: "xl",
                "persist-selected": "",
                "search-provider": geocodingInfoForSearchLimited,
                onSetLocation: setLocationFromSearch,
                onError: _cache[2] || (_cache[2] = (error) => searchErrorMessage.value = error)
              }, null, 8, ["modelValue"])
            ])
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["horizontal", "current-colormap", "color-map", "start-value", "end-value", "molecule-label", "cbar-scale"]),
    _createElementVNode("div", _hoisted_4, [
      _createVNode(_component_v_slider, {
        class: "time-slider",
        modelValue: _unref(timeIndex),
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (_isRef(timeIndex) ? (timeIndex).value = $event : null)),
        min: _unref(minIndex),
        max: _unref(maxIndex),
        step: 1,
        color: "#068ede95",
        "thumb-label": "always",
        "track-size": 10,
        "show-ticks": "always",
        "hide-details": "",
        onEnd: _cache[4] || (_cache[4] = () => {
          timeSliderUsedCount.value += 1;
          // if (map) {
          //   setLayerVisibility(map as Map, activeLayer, true);
          // }
        })
      }, {
        "thumb-label": _withCtx(() => [
          _createElementVNode("div", _hoisted_5, _toDisplayString(thumbLabel.value), 1)
        ]),
        _: 1
      }, 8, ["modelValue", "min", "max"]),
      _createVNode(_component_icon_button, {
        class: "play-pause",
        "fa-icon": playing.value ? 'pause' : 'play',
        "fa-size": "sm",
        onActivate: _cache[5] || (_cache[5] = ($event: any) => (playing.value = !playing.value))
      }, null, 8, ["fa-icon"])
    ]),
    _createElementVNode("div", _hoisted_6, [
      _createVNode(_component_map_controls, {
        class: "flex-grow-1",
        onMolecule: _cache[6] || (_cache[6] = (mol) => {
          molecule.value = mol;
          // if (map) {
          //   setLayerVisibility(map as Map, activeLayer, true);
          // }
        })
      })
    ])
  ]))
}
}

})